import { Validators } from '@angular/forms';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { FormValidators } from '~shared/validators/form-validators';
import {
  COMPANY_NAME_PATTERN,
  DIGITS_ONLY_PATTERN,
  ELECTRONIC_INVOICE_ID,
  EMAIL_PATTERN,
  GERMAN_SYMBOLS_DIGITS_PATTERN,
  GERMAN_SYMBOLS_PATTERN,
  ITALIAN_SYMBOLS_DIGITS_PATTERN,
  ITALIAN_SYMBOLS_PATTERN,
  LATIN_SYMBOLS_DIGITS_PATTERN,
  LATIN_SYMBOLS_PATTERN,
  NOT_ONLY_ADDRESS_SYMBOLS_PATTERN,
  NOT_ONLY_SYMBOLS_PATTERN,
  PASSWORD_PATTERN,
  PASSWORD_PATTERN_LENGTH,
  PORTUGUESE_SYMBOLS_DIGITS_PATTERN,
  PORTUGUESE_SYMBOLS_PATTERN,
  POSTAL_CODE_PATTERNS,
  POSTCODE_PATTERN,
  POSTCODE_PATTERN_DE,
  POSTCODE_PATTERN_ES,
  POSTCODE_PATTERN_IT,
  POSTCODE_PATTERN_PT,
  POSTCODE_PATTERN_NL,
  POSTCODE_PATTERN_FR,
  PROVINCE_PATTERN,
  SPANISH_COMPANY_NAME_PATTERN_LIST,
  SPANISH_LATIN_SYMBOLS_PATTERN_LIST,
  SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN,
  SPANISH_SYMBOLS_DIGITS_PATTERN,
  SPANISH_SYMBOLS_PATTERN_LIST,
  TAX_NUMBER_DE_PATTERN_LIST,
  TAX_NUMBER_PATTERN_LISTS,
  VALID_DATE_PATTERN,
  VALID_DAY_PATTERN,
  VALID_MONTH_PATTERN,
  VALID_YEAR_PATTERN,
  VAT_DE_PATTERN,
  VAT_ITALY_PATTERN_TIER_TWO,
  VAT_PORTUGAL_PATTERN_TIER_TWO,
  KVK_NETHERLANDS_PATTERN,
  DUTCH_SYMBOLS_DIGITS_PATTERN,
  DUTCH_SYMBOLS_PATTERN,
  VAT_DUTCH_PATTERN_TIER_TWO,
  SIREN_FRANCE_PATTERN,
  DIGITS_WITH_CODE_PATTERN,
} from '../validators/pattern-validators';
import { CountryCode } from './country-code.enum';
import { VALID_AGE } from './general.constants';

const { required, maxLength, pattern, minLength } = Validators;

export const FIELDS_LENGTHS = {
  companyName: 200,
  italianCompanyName: 80,
  portugueseCompanyName: 80,
  dutchCompanyName: 80,
  firstName: 100,
  lastName: 100,
  street: 140,
  portugueseStreet: 100,
  dutchStreet: 100,
  houseNumber: 10,
  addressLine2: 150,
  city: 100,
  verificationPostalCode: 15,
  phoneMax: 15,
  phoneMin: 6,
  legalAddressProvince: 2,
};

export const POSTAL_CODE_VALIDATION_RULES = {
  pattern: POSTCODE_PATTERN,
  maxLength: 15,

  [CountryCode.Germany]: {
    pattern: POSTCODE_PATTERN_DE,
    minLength: 5,
    maxLength: 5,
  },
  [CountryCode.Spain]: {
    pattern: POSTCODE_PATTERN_ES,
    minLength: 5,
    maxLength: 5,
  },
  [CountryCode.Italy]: {
    pattern: POSTCODE_PATTERN_IT,
    minLength: 5,
    maxLength: 5,
  },
  [CountryCode.Portugal]: {
    pattern: POSTCODE_PATTERN_PT,
    minLength: 8,
    maxLength: 8,
  },
  [CountryCode.Netherlands]: {
    pattern: POSTCODE_PATTERN_NL,
    minLength: 6,
    maxLength: 7,
  },
  [CountryCode.France]: {
    pattern: POSTCODE_PATTERN_FR,
    minLength: 5,
    maxLength: 5,
  },
};

export const VALIDATION_RULES = {
  companyName: {
    required,
    pattern: pattern(COMPANY_NAME_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.companyName),
  },
  vatNumber: {
    pattern: pattern(VAT_DE_PATTERN),
  },
  taxNumber: {
    pattern: FormValidators.patterns(TAX_NUMBER_DE_PATTERN_LIST, 'some'),
  },
  spanishTaxNumber: {
    pattern: FormValidators.patterns(TAX_NUMBER_DE_PATTERN_LIST, 'some'),
  },
  firstName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  street: {
    required,
    pattern: pattern(LATIN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.street),
  },
  houseNumber: {
    required,
    pattern: pattern(GERMAN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  addressLine2: {
    pattern: pattern(LATIN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.addressLine2),
  },
  postalCode: {
    required,
  },
  verificationPostalCode: {
    required,
    maxLength: maxLength(FIELDS_LENGTHS.verificationPostalCode),
  },
  city: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  country: {
    required,
  },
  phone: {
    pattern: pattern(DIGITS_ONLY_PATTERN),
    minLength: minLength(FIELDS_LENGTHS.phoneMin),
    maxLength: maxLength(FIELDS_LENGTHS.phoneMax),
  },
  phoneWithCode: {
    pattern: pattern(DIGITS_WITH_CODE_PATTERN),
    minLength: minLength(FIELDS_LENGTHS.phoneMin),
    maxLength: maxLength(FIELDS_LENGTHS.phoneMax),
  },
  email: {
    required,
    pattern: pattern(EMAIL_PATTERN),
  },
  newEmail: {
    required,
    pattern: pattern(EMAIL_PATTERN),
  },
  emailForLogin: {
    required,
  },
  password: {
    pattern: pattern(PASSWORD_PATTERN),
    security: FormValidators.security({
      length: PASSWORD_PATTERN_LENGTH,
    }),
  },
  confirmationPassword: {
    required,
  },
  passwordForLogin: {
    required,
  },
  oldPassword: {
    required,
  },
  newPassword: {
    required,
    pattern: pattern(PASSWORD_PATTERN),
    security: FormValidators.security({
      length: PASSWORD_PATTERN_LENGTH,
    }),
  },
  newPasswordConfirmation: {
    required,
  },
  entrepreneurStatusConfirmation: {
    required: FormValidators.mandatoryCheckbox,
  },
  isDay: {
    pattern: FormValidators.pattern(VALID_DAY_PATTERN, 'datePattern'),
  },
  isMonth: {
    pattern: FormValidators.pattern(VALID_MONTH_PATTERN, 'datePattern'),
  },
  isYear: {
    pattern: FormValidators.pattern(VALID_YEAR_PATTERN, 'datePattern'),
  },
  dateOfBirth: {
    pattern: FormValidators.pattern(VALID_DATE_PATTERN, 'datePattern'),
    minAge: FormValidators.minAge(VALID_AGE),
  },
};

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_GERMANY = () => ({
  firstName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    pattern: pattern(GERMAN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.street),
  },
  legalAddressBuildingNumber: {
    required,
    pattern: pattern(GERMAN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: pattern(GERMAN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: {
    required,
  },
  legalAddressPostalCode: {
    required,
    pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]),
  },
  businessName: {
    required,
    pattern: pattern(COMPANY_NAME_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.companyName),
  },
});

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_SPAIN = () => ({
  firstName: {
    required,
    pattern: FormValidators.patterns(SPANISH_LATIN_SYMBOLS_PATTERN_LIST, 'some'),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: FormValidators.patterns(SPANISH_LATIN_SYMBOLS_PATTERN_LIST, 'some'),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    pattern: FormValidators.patterns(SPANISH_SYMBOLS_PATTERN_LIST, 'some'),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.street),
  },
  legalAddressBuildingNumber: {
    required,
    pattern: pattern(SPANISH_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: FormValidators.patterns(SPANISH_SYMBOLS_PATTERN_LIST, 'some'),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: {
    required,
  },
  legalAddressPostalCode: {
    required,
    pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]),
  },
  businessName: {
    required,
    pattern: FormValidators.patterns(SPANISH_COMPANY_NAME_PATTERN_LIST, 'some'),
    notOnlySymbols: FormValidators.notOnlySymbols(SPANISH_NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.companyName),
  },
  taxNumber: {
    required,
    pattern: FormValidators.patterns(TAX_NUMBER_PATTERN_LISTS[MarketDetectionUtil.getCountryCode()], 'some'),
  },
});

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_ITALY = () => ({
  firstName: {
    required,
    pattern: pattern(ITALIAN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(ITALIAN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    pattern: pattern(ITALIAN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.street),
  },
  legalAddressBuildingNumber: {
    required,
    pattern: pattern(ITALIAN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: pattern(ITALIAN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: { required },
  legalAddressPostalCode: { required, pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]) },
  businessName: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.italianCompanyName),
  },
  vatNumber: { required, pattern: pattern(VAT_ITALY_PATTERN_TIER_TWO) },
  legalElectronicInvoiceId: {
    pattern: pattern(ELECTRONIC_INVOICE_ID),
  },
  legalEmailAddress: {
    pattern: pattern(EMAIL_PATTERN),
  },
  legalAddressProvince: {
    required,
    pattern: pattern(PROVINCE_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.legalAddressProvince),
  },
});

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_PORTUGAL = () => ({
  firstName: {
    required,
    pattern: pattern(PORTUGUESE_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(PORTUGUESE_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.portugueseStreet),
  },
  legalAddressBuildingNumber: {
    required,
    pattern: pattern(PORTUGUESE_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: pattern(PORTUGUESE_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: { required },
  legalAddressPostalCode: { required, pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]) },
  businessName: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.portugueseCompanyName),
  },
  vatNumber: { required, pattern: pattern(VAT_PORTUGAL_PATTERN_TIER_TWO) },
});

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_NETHERLANDS = () => ({
  firstName: {
    required,
    pattern: pattern(DUTCH_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(DUTCH_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.dutchStreet),
  },
  legalAddressBuildingNumber: {
    required,
    pattern: pattern(DUTCH_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: pattern(DUTCH_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: { required },
  legalAddressPostalCode: { required, pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]) },
  businessName: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.dutchCompanyName),
  },
  kvkNumber: { required, pattern: pattern(KVK_NETHERLANDS_PATTERN) },
  vatNumber: { required, pattern: pattern(VAT_DUTCH_PATTERN_TIER_TWO) },
});

export const TIER_TWO_REGISTRATION_VALIDATION_RULES_FRANCE = () => ({
  firstName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.firstName),
  },
  lastName: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.lastName),
  },
  legalAddressStreet: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.dutchStreet),
  },
  legalAddressBuildingNumber: {
    pattern: pattern(LATIN_SYMBOLS_DIGITS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_ADDRESS_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.houseNumber),
  },
  legalAddressCity: {
    required,
    pattern: pattern(LATIN_SYMBOLS_PATTERN),
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.city),
  },
  legalAddressCountry: { required },
  legalAddressPostalCode: { required, pattern: pattern(POSTAL_CODE_PATTERNS[MarketDetectionUtil.getCountryCode()]) },
  businessName: {
    required,
    notOnlySymbols: FormValidators.notOnlySymbols(NOT_ONLY_SYMBOLS_PATTERN),
    maxLength: maxLength(FIELDS_LENGTHS.dutchCompanyName),
  },
  sirenNumber: { required, pattern: pattern(SIREN_FRANCE_PATTERN) },
});
